import { memo, useCallback, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import ReactDatePicker from "../../../../../../common/components/datePicker/ReactDatePicker";
import { useGetS3Files } from "../../../../../telemetry/hooks/useGetS3Files";
import { useRecordingsAvailableDates } from "../../../../../telemetry/hooks/useRecordingsAvailableDates";
import LiveViewRecordingsVideoListHeader from "./LiveViewRecordingsVideoListHeader";
import LiveViewRecordingsVideoListItem from "./LiveViewRecordingsVideoListItem";
import LiveViewRecordingsVideoListNoS3Videos from "./LiveViewRecordingsVideoListNoS3Videos";
import {
  RecordingsSortFilter,
  sortFilesByMode,
  SortMode,
} from "./RecordingsSortFilter";

dayjs.extend(utc);

dayjs.extend(timezone);

interface LiveViewRecordingsVideoListProps {
  deviceId: string;
  nodeId: string;
  date: string;
  onRefresh?: () => void;
  onPlay?: (url: string) => void;
  listDate: Date;
  setListDate: (date: Date) => void;
  timeZone: string;
}

const LiveViewRecordingsVideoList = ({
  deviceId,
  nodeId,
  onRefresh,
  listDate,
  setListDate,
  timeZone,
}: LiveViewRecordingsVideoListProps): JSX.Element => {
  const cleanNodeId = nodeId.replace(/#/g, "_");
  const cleanDeviceId = deviceId.replace(/^DE#/, "");
  const [sortMode, setSortMode] = useState<SortMode>("");
  const [idOfPlayingVideo, setIdOfPlayingVideo] = useState<string>("");
  const dateForQuery = useMemo(
    () =>
      timeZone
        ? // Keep the selected calendar day when mapping to device TZ to avoid off-by-one
          dayjs(listDate).tz(timeZone, true).format("YYYY/MM/DD")
        : dayjs(listDate).format("YYYY/MM/DD"),
    [listDate, timeZone]
  );

  const { dateCountMap, refetch: refetchAvailableDates } =
    useRecordingsAvailableDates(cleanDeviceId, cleanNodeId);

  const { files, loading, refetch } = useGetS3Files(
    cleanDeviceId,
    cleanNodeId,
    dateForQuery,
    {
      skip: !cleanDeviceId || !cleanNodeId || !dateForQuery,
    }
  );

  const minSelectableDate = useMemo(() => {
    return new Date(0);
  }, []);

  const maxSelectableDate = useMemo(() => {
    if (!timeZone) {
      return dayjs().endOf("day").toDate();
    }
    // Compute the device "today" (by device TZ), then represent that same calendar day
    // in the user's local timezone so the calendar caps by device day, not local day.

    const deviceTodayStr = dayjs().tz(timeZone).format("YYYY-MM-DD");

    return dayjs(deviceTodayStr).endOf("day").toDate();
  }, [timeZone]);

  const renderDayContents = useCallback(
    (day: number, date?: Date) => {
      if (!date || !dateCountMap || dateCountMap.size === 0) {
        return day;
      }

      // Align day cell to device TZ without shifting the calendar day
      const dateKey = timeZone
        ? dayjs(date).tz(timeZone, true).format("YYYY/MM/DD")
        : dayjs(date).format("YYYY/MM/DD");
      const count = dateCountMap.get(dateKey);

      if (!count) {
        return day;
      }

      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: "14px",
              lineHeight: 1,
            }}
          >
            {day}
          </Typography>

          <Box
            sx={{
              position: "absolute",
              top: "0px",
              right: "-7px",
              minWidth: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: theme => theme.palette.success.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: count > 99 ? "8px" : "10px",
              color: theme => theme.palette.otherBackground.main,
              fontWeight: 600,
              lineHeight: 1,
              paddingX: count > 99 ? 0.25 : 0,
            }}
          >
            {count > 99 ? "99+" : count}
          </Box>
        </Box>
      );
    },
    [dateCountMap, timeZone]
  );

  const filterDateByDeviceTz = useCallback(
    (date: Date) => {
      if (!timeZone) {
        return (
          dayjs(date).isSame(dayjs(), "day") ||
          dayjs(date).isBefore(dayjs(), "day")
        );
      }

      const deviceNow = dayjs().tz(timeZone);
      const deviceDate = dayjs(date).tz(timeZone);

      return (
        deviceDate.isSame(deviceNow, "day") ||
        deviceDate.isBefore(deviceNow, "day")
      );
    },
    [timeZone]
  );

  const onRefreshHandler = useCallback(() => {
    refetch();

    refetchAvailableDates();

    setSortMode("");

    setIdOfPlayingVideo("");
  }, [refetch, refetchAvailableDates]);

  const sortModeHandler = useCallback((sortMode: SortMode) => {
    setIdOfPlayingVideo("");

    setSortMode(sortMode);
  }, []);

  const sortedFiles = useMemo(() => {
    return sortFilesByMode(files, sortMode);
  }, [files, sortMode]);

  const isListEmpty = useMemo(() => {
    return sortedFiles.length === 0;
  }, [sortedFiles]);

  return (
    <Box
      sx={{
        borderRadius: "6px",
        overflow: "hidden",
        borderWidth: "1px !important",
        border: theme => `1px solid ${theme.palette.otherOutlineBorder.main}`,
      }}
    >
      <LiveViewRecordingsVideoListHeader
        refetch={refetch}
        onRefresh={onRefreshHandler}
      />
      <Divider sx={{ height: "1px", borderBottomWidth: "1px" }} />

      <Box sx={{ px: 2, py: 1 }}>
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexWrap: "nowrap",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <RecordingsSortFilter value={sortMode} onChange={sortModeHandler} />

          <ReactDatePicker
            mode="single"
            placeholderText="Choose Date"
            value={listDate}
            onChange={val => {
              setIdOfPlayingVideo("");

              setListDate((val as Date) ?? new Date());
            }}
            onChangeRaw={event => {
              event.preventDefault();
            }}
            isClearable={false}
            minDate={minSelectableDate}
            maxDate={maxSelectableDate}
            filterDate={filterDateByDeviceTz}
            calendarClassName="no-today-highlight"
            renderDayContents={renderDayContents}
          />
        </Box>

        <Divider sx={{ mx: -2, borderBottomWidth: "1px" }} />

        {loading ? (
          <Box
            sx={{
              height: "220px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">Loading...</Typography>
          </Box>
        ) : !isListEmpty ? (
          <Stack spacing={1.5} sx={{ py: 1 }} role="list">
            {sortedFiles.map(file => (
              <LiveViewRecordingsVideoListItem
                timeZone={timeZone}
                file={file}
                idOfPlayingVideo={idOfPlayingVideo}
                setIdOfPlayingVideo={setIdOfPlayingVideo}
              />
            ))}
          </Stack>
        ) : (
          <LiveViewRecordingsVideoListNoS3Videos />
        )}
      </Box>
    </Box>
  );
};

export default memo(LiveViewRecordingsVideoList);
